import { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import api from "@/utils/api";
import { servicesOptions } from "@/data/services";

const steps = ["Coordonnées", "Type de demande", "Détails", "Accès", "Validation"];

const requestTypes = [
  { value: "urgence", label: "Urgence" },
  { value: "programme", label: "Programmé" },
  { value: "devis", label: "Devis" },
];

const logementTypes = ["Maison", "Appartement", "Commerce", "Bureau", "Autre"];
const accessOptions = ["Facile", "Moyen", "Difficile"];
const presenceOptions = ["Présent sur place", "Clés à récupérer", "Accès par gardien"];

const ContactForm = () => {
  const sourcePage = useMemo(() => sessionStorage.getItem("sourcePage") || "direct", []);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    nom: "",
    telephone: "",
    email: "",
    adresse: "",
    code_postal: "",
    ville: "",
    type_demande: "",
    service: "",
    description: "",
    urgence: "standard",
    date_souhaitee: "",
    type_logement: "",
    facilite_acces: "",
    presence: "",
    consentement: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [addressQuery, setAddressQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [manualAddress, setManualAddress] = useState(false);

  useEffect(() => {
    if (!addressQuery || manualAddress) {
      setSuggestions([]);
      return;
    }
    const controller = new AbortController();
    const fetchSuggestions = async () => {
      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(addressQuery)}&limit=5`,
          { signal: controller.signal },
        );
        const data = await response.json();
        setSuggestions(data.features || []);
      } catch (error) {
        if (error.name != "AbortError") {
          setSuggestions([]);
        }
      }
    };
    fetchSuggestions();
    return () => controller.abort();
  }, [addressQuery, manualAddress]);

  const handleChange = (field) => (event) => {
    const value = event.target.type == "checkbox" ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSuggestionSelect = (suggestion) => {
    const { label, postcode, city } = suggestion.properties;
    setFormData((prev) => ({
      ...prev,
      adresse: label,
      code_postal: postcode,
      ville: city,
    }));
    setAddressQuery(label);
    setSuggestions([]);
  };

  const validateStep = () => {
    const newErrors = {};
    if (step === 0) {
      if (!formData.nom) newErrors.nom = "Nom requis";
      if (!formData.telephone) newErrors.telephone = "Téléphone requis";
      if (!formData.email) newErrors.email = "Email requis";
      if (!formData.adresse) newErrors.adresse = "Adresse requise";
      if (!formData.code_postal) newErrors.code_postal = "Code postal requis";
      if (!formData.ville) newErrors.ville = "Ville requise";
    }
    if (step === 1) {
      if (!formData.type_demande) newErrors.type_demande = "Sélection requise";
      if (!formData.service) newErrors.service = "Service requis";
    }
    if (step === 2 && !formData.description) newErrors.description = "Description requise";
    if (step === 3) {
      if (!formData.type_logement) newErrors.type_logement = "Type requis";
      if (!formData.facilite_acces) newErrors.facilite_acces = "Choix requis";
      if (!formData.presence) newErrors.presence = "Choix requis";
    }
    if (step === 4 && !formData.consentement) newErrors.consentement = "Consentement requis";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrevious = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setIsSubmitting(true);
    try {
      await api.post("/api/leads", {
        source_page: sourcePage,
        type_demande: formData.type_demande,
        service: formData.service,
        nom: formData.nom,
        telephone: formData.telephone,
        email: formData.email,
        adresse: formData.adresse,
        code_postal: formData.code_postal,
        ville: formData.ville,
        description: formData.description,
        urgence: formData.urgence,
        date_souhaitee: formData.date_souhaitee,
        type_logement: formData.type_logement,
        facilite_acces: formData.facilite_acces,
        presence: formData.presence,
        consentement: formData.consentement,
      });
      setSubmitted(true);
    } catch (error) {
      setErrors({ submit: "Impossible d’envoyer la demande. Réessayez." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-brand-blue/15 p-8 rounded-sm" data-testid="contact-success">
        <Check className="text-brand-red" size={32} data-testid="contact-success-icon" />
        <h3 className="font-heading text-2xl uppercase mt-4" data-testid="contact-success-title">
          Merci, votre demande a bien été envoyée !
        </h3>
        <p className="mt-3 text-brand-ink/70" data-testid="contact-success-text">
          Nos équipes vous recontactent rapidement. En cas d’urgence, appelez-nous immédiatement.
        </p>
        <a
          href="tel:0184160307"
          className="inline-flex mt-6 bg-brand-red text-white px-5 py-3 rounded-full uppercase text-xs font-bold"
          data-testid="contact-success-call"
        >
          Appelez 01 84 16 03 07
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white border border-brand-blue/15 p-6 md:p-8 rounded-sm" data-testid="contact-form">
      <div className="flex items-center justify-between" data-testid="form-steps">
        <div>
          <p className="text-xs uppercase tracking-wider text-brand-ink/60" data-testid="form-step-label">
            Étape {step + 1} / {steps.length}
          </p>
          <h3 className="font-heading text-2xl uppercase" data-testid="form-step-title">
            {steps[step]}
          </h3>
        </div>
        <span className="text-xs uppercase font-semibold text-brand-blue" data-testid="form-progress-text">
          {steps[step]}
        </span>
      </div>
      <div className="mt-6" data-testid="form-progress-bar">
        <div className="h-1 bg-brand-blue/10 rounded-full">
          <div
            className="h-1 bg-brand-red rounded-full"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            data-testid="form-progress-indicator"
          />
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {step === 0 && (
          <div className="space-y-4" data-testid="step-coordinates">
            <label className="block text-sm uppercase font-semibold" data-testid="name-label">
              Nom complet *
            </label>
            <input
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              value={formData.nom}
              onChange={handleChange("nom")}
              data-testid="name-input"
            />
            {errors.nom && (
              <p className="text-xs text-brand-red" data-testid="error-nom">
                {errors.nom}
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm uppercase font-semibold" data-testid="phone-label">
                  Téléphone *
                </label>
                <input
                  className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                  value={formData.telephone}
                  onChange={handleChange("telephone")}
                  data-testid="phone-input"
                />
                {errors.telephone && (
                  <p className="text-xs text-brand-red" data-testid="error-telephone">
                    {errors.telephone}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm uppercase font-semibold" data-testid="email-label">
                  Email *
                </label>
                <input
                  type="email"
                  className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                  value={formData.email}
                  onChange={handleChange("email")}
                  data-testid="email-input"
                />
                {errors.email && (
                  <p className="text-xs text-brand-red" data-testid="error-email">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
            <label className="block text-sm uppercase font-semibold" data-testid="address-label">
              Adresse *
            </label>
            <input
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              value={addressQuery}
              onChange={(event) => {
                setAddressQuery(event.target.value);
                handleChange("adresse")({ target: { value: event.target.value } });
              }}
              placeholder="Entrez votre adresse"
              data-testid="address-input"
            />
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={manualAddress}
                onChange={(event) => setManualAddress(event.target.checked)}
                data-testid="manual-address-toggle"
              />
              <span className="text-xs" data-testid="manual-address-text">
                Renseigner manuellement
              </span>
            </div>
            {suggestions.length > 0 && (
              <div className="border border-brand-blue/15 rounded-sm mt-2 overflow-hidden" data-testid="address-suggestions">
                {suggestions.map((item, index) => (
                  <button
                    key={item.properties.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-brand-blue/5 text-sm"
                    onClick={() => handleSuggestionSelect(item)}
                    data-testid={`address-suggestion-${index}`}
                  >
                    {item.properties.label}
                  </button>
                ))}
              </div>
            )}
            {errors.adresse && (
              <p className="text-xs text-brand-red" data-testid="error-adresse">
                {errors.adresse}
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm uppercase font-semibold" data-testid="postal-label">
                  Code postal *
                </label>
                <input
                  className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                  value={formData.code_postal}
                  onChange={handleChange("code_postal")}
                  data-testid="postal-input"
                />
                {errors.code_postal && (
                  <p className="text-xs text-brand-red" data-testid="error-code-postal">
                    {errors.code_postal}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm uppercase font-semibold" data-testid="city-label">
                  Ville *
                </label>
                <input
                  className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                  value={formData.ville}
                  onChange={handleChange("ville")}
                  data-testid="city-input"
                />
                {errors.ville && (
                  <p className="text-xs text-brand-red" data-testid="error-ville">
                    {errors.ville}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="step-type-demande">
            <div className="grid md:grid-cols-3 gap-4">
              {requestTypes.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange("type_demande")({ target: { value: option.value } })}
                  className={`border p-4 rounded-sm text-left ${
                    formData.type_demande === option.value
                      ? "border-brand-red bg-brand-red/10"
                      : "border-brand-blue/15"
                  }`}
                  data-testid={`type-demande-${option.value}`}
                >
                  <p className="font-semibold" data-testid={`type-demande-${option.value}-title`}>
                    {option.label}
                  </p>
                  <p className="text-sm text-brand-ink/60" data-testid={`type-demande-${option.value}-desc`}>
                    {option.value === "urgence" ? "Intervention rapide." : "Planification selon vos besoins."}
                  </p>
                </button>
              ))}
            </div>
            {errors.type_demande && (
              <p className="text-xs text-brand-red" data-testid="error-type-demande">
                {errors.type_demande}
              </p>
            )}
            <label className="block text-sm uppercase font-semibold" data-testid="service-label">
              Service demandé *
            </label>
            <select
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              value={formData.service}
              onChange={handleChange("service")}
              data-testid="service-select"
            >
              <option value="">Sélectionnez un service</option>
              {servicesOptions.map((service) => (
                <option key={service.slug} value={service.title} data-testid={`service-option-${service.slug}`}>
                  {service.title}
                </option>
              ))}
            </select>
            {errors.service && (
              <p className="text-xs text-brand-red" data-testid="error-service">
                {errors.service}
              </p>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="step-details">
            <label className="block text-sm uppercase font-semibold" data-testid="description-label">
              Description de la demande *
            </label>
            <textarea
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2 min-h-[140px]"
              value={formData.description}
              onChange={handleChange("description")}
              data-testid="description-textarea"
            />
            {errors.description && (
              <p className="text-xs text-brand-red" data-testid="error-description">
                {errors.description}
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm uppercase font-semibold" data-testid="urgence-label">
                  Niveau d’urgence
                </label>
                <select
                  className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                  value={formData.urgence}
                  onChange={handleChange("urgence")}
                  data-testid="urgence-select"
                >
                  <option value="standard">Standard</option>
                  <option value="immediat">Immédiat (24h)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm uppercase font-semibold" data-testid="date-label">
                  Date souhaitée
                </label>
                <input
                  type="date"
                  className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                  value={formData.date_souhaitee}
                  onChange={handleChange("date_souhaitee")}
                  data-testid="date-input"
                />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="step-access">
            <label className="block text-sm uppercase font-semibold" data-testid="logement-label">
              Type de logement *
            </label>
            <select
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              value={formData.type_logement}
              onChange={handleChange("type_logement")}
              data-testid="logement-select"
            >
              <option value="">Sélectionnez</option>
              {logementTypes.map((type) => (
                <option key={type} value={type} data-testid={`logement-option-${type}`}>
                  {type}
                </option>
              ))}
            </select>
            {errors.type_logement && (
              <p className="text-xs text-brand-red" data-testid="error-logement">
                {errors.type_logement}
              </p>
            )}
            <label className="block text-sm uppercase font-semibold" data-testid="access-label">
              Facilité d’accès *
            </label>
            <select
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              value={formData.facilite_acces}
              onChange={handleChange("facilite_acces")}
              data-testid="access-select"
            >
              <option value="">Sélectionnez</option>
              {accessOptions.map((option) => (
                <option key={option} value={option} data-testid={`access-option-${option}`}>
                  {option}
                </option>
              ))}
            </select>
            {errors.facilite_acces && (
              <p className="text-xs text-brand-red" data-testid="error-access">
                {errors.facilite_acces}
              </p>
            )}
            <label className="block text-sm uppercase font-semibold" data-testid="presence-label">
              Présence / Accès *
            </label>
            <select
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              value={formData.presence}
              onChange={handleChange("presence")}
              data-testid="presence-select"
            >
              <option value="">Sélectionnez</option>
              {presenceOptions.map((option) => (
                <option key={option} value={option} data-testid={`presence-option-${option}`}>
                  {option}
                </option>
              ))}
            </select>
            {errors.presence && (
              <p className="text-xs text-brand-red" data-testid="error-presence">
                {errors.presence}
              </p>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="step-validation">
            <div className="border border-brand-blue/15 rounded-sm p-4 bg-brand-blue/5" data-testid="summary-box">
              <p className="text-sm" data-testid="summary-name">
                <strong>Nom :</strong> {formData.nom}
              </p>
              <p className="text-sm" data-testid="summary-contact">
                <strong>Contact :</strong> {formData.telephone} • {formData.email}
              </p>
              <p className="text-sm" data-testid="summary-service">
                <strong>Service :</strong> {formData.service}
              </p>
              <p className="text-sm" data-testid="summary-address">
                <strong>Adresse :</strong> {formData.adresse}, {formData.code_postal} {formData.ville}
              </p>
            </div>
            <label className="flex items-start gap-2 text-xs" data-testid="consent-label">
              <input
                type="checkbox"
                checked={formData.consentement}
                onChange={handleChange("consentement")}
                data-testid="consent-checkbox"
              />
              J’accepte que mes données soient utilisées pour traiter ma demande.
            </label>
            {errors.consentement && (
              <p className="text-xs text-brand-red" data-testid="error-consentement">
                {errors.consentement}
              </p>
            )}
          </div>
        )}
      </div>

      {errors.submit && (
        <p className="text-xs text-brand-red mt-4" data-testid="error-submit">
          {errors.submit}
        </p>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button
          type="button"
          onClick={handlePrevious}
          className="border border-brand-blue/15 px-4 py-2 rounded-full text-sm uppercase"
          data-testid="form-prev-button"
          disabled={step === 0}
        >
          Retour
        </button>
        {step < steps.length - 1 ? (
          <button
            type="button"
            onClick={handleNext}
            className="bg-brand-red text-white px-5 py-2 rounded-full text-sm uppercase font-semibold"
            data-testid="form-next-button"
          >
            Continuer
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            className="bg-brand-red text-white px-5 py-2 rounded-full text-sm uppercase font-semibold"
            data-testid="form-submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Envoi..." : "Envoyer la demande"}
          </button>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
